import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { InkButton } from '../components/InkButton';
import { InkedTitle } from '../components/InkedTitle';
import { Reveal } from '../components/Reveal';
import { WorkCard } from '../components/WorkCard';
import { useBooking } from '../context/BookingContext';
import { getArtistBySlug, getWorksByArtist, styleLabels } from '../data';
import { useLang } from '../hooks/useLang';
import { NotFoundPage } from './NotFound';

export function ArtistDetailPage() {
  const { t } = useTranslation();
  const lang = useLang();
  const { slug } = useParams<{ slug: string }>();
  const artist = slug ? getArtistBySlug(slug) : undefined;
  const { openBooking } = useBooking();

  if (!artist) return <NotFoundPage />;
  const artistWorks = getWorksByArtist(artist.slug);

  return (
    <>
      <section className="px-6 pt-32 pb-12 md:px-10 md:pt-40 md:pb-16">
        <div className="mx-auto max-w-[1800px]">
        <Link
          to="/artisti"
          className="group inline-flex items-center gap-2 text-base font-display lowercase text-ink-muted hover:text-ink-paper transition-colors"
        >
          <span aria-hidden className="transition-transform group-hover:-translate-x-1">
            ←
          </span>
          <span>{t('artist.back')}</span>
        </Link>

        <div className="mt-10 grid gap-10 md:grid-cols-12 items-end">
          <div className="md:col-span-7">
            <p className="text-base font-display lowercase text-ink-muted tracking-tight">
              {artist.styles.map((s) => styleLabels[s][lang].toLowerCase()).join(' · ')}
            </p>
            <InkedTitle
              as="h1"
              immediate
              delay={0.15}
              className="mt-6 font-display tracking-display leading-[0.85] text-[clamp(3.5rem,12vw,12rem)] lowercase"
            >
              {artist.name}
            </InkedTitle>
          </div>
          <div className="md:col-span-5 relative aspect-[4/5] overflow-hidden bg-ink-line">
            <img
              src={artist.portrait}
              alt={artist.name}
              className="h-full w-full object-cover ink-photo"
            />
          </div>
        </div>
        </div>
      </section>

      <section className="px-6 py-12 md:px-10 md:py-20 border-t border-ink-line/40">
        <div className="mx-auto max-w-[1800px] grid gap-10 md:grid-cols-12">
          <Reveal className="md:col-span-3">
            <dl className="grid gap-6">
              <Meta label={t('artist.sinceLabel')} value={String(artist.since)} />
              <Meta label={t('artist.originLabel')} value={artist.origin} />
              <Meta label={t('artist.basedInLabel')} value={artist.basedIn} />
              <Meta
                label={t('artist.stylesLabel')}
                value={artist.styles.map((s) => styleLabels[s][lang]).join(', ')}
              />
              <div>
                <dt className="text-sm font-display lowercase text-ink-muted tracking-tight">
                  {t('common.instagram').toLowerCase()}
                </dt>
                <dd className="mt-2">
                  <a
                    href={`https://instagram.com/${artist.instagram}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-base hover:text-ink-paper transition-colors"
                  >
                    @{artist.instagram} ↗
                  </a>
                </dd>
              </div>
            </dl>
          </Reveal>

          <Reveal className="md:col-span-7 md:col-start-5" delay={0.1}>
            <p className="text-base font-display lowercase text-ink-muted tracking-tight">
              {t('artist.manifesto').toLowerCase()}
            </p>
            <p className="mt-4 font-display tracking-display leading-[1.05] text-3xl md:text-5xl text-ink-paper">
              «{artist.manifesto[lang]}»
            </p>
            <p className="mt-10 text-base md:text-lg text-ink-paper/85 leading-relaxed max-w-2xl">
              {artist.bio[lang]}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-x-10 gap-y-6">
              <InkButton
                as="button"
                size="lg"
                onClick={() => openBooking({ artistSlug: artist.slug })}
              >
                {t('artist.bookCta', { name: artist.name })}
              </InkButton>
              <InkButton
                as="a"
                size="md"
                variant="link"
                href={`https://instagram.com/${artist.instagram}`}
                target="_blank"
                rel="noreferrer"
                suffix="↗"
              >
                {t('artist.viewInstagram')}
              </InkButton>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 py-16 md:px-10 md:py-24 border-t border-ink-line/40">
        <div className="mx-auto max-w-[1800px]">
        <div className="flex items-end justify-between gap-8 mb-12 md:mb-16">
          <div>
            <p className="text-base font-display lowercase text-ink-muted tracking-tight">
              {t('artist.worksLabel').toLowerCase()} · {artistWorks.length}
            </p>
            <h2 className="mt-4 font-display tracking-display leading-[0.95] text-3xl md:text-5xl">
              {artist.name}
            </h2>
          </div>
        </div>

        <div className="grid gap-x-6 gap-y-12 md:gap-x-8 md:gap-y-16 grid-cols-2 md:grid-cols-3">
          {artistWorks.map((work, i) => (
            <Reveal key={work.id} delay={(i % 3) * 0.05}>
              <WorkCard work={work} index={i} />
            </Reveal>
          ))}
        </div>
        </div>
      </section>

    </>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-sm font-display lowercase text-ink-muted tracking-tight">{label.toLowerCase()}</dt>
      <dd className="mt-2 text-base text-ink-paper">{value}</dd>
    </div>
  );
}
